import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { getFactories } from '../../../data/factoriesData';
import { ErrorModal } from '../../../components/ui/ErrorModal';


// TODO: Fare calculation provision — indicative min/max fare to be added when freight rates confirmed
const RATE_PER_TRUCK_PER_KM = null; // reserved for future fare calc
const TRUCK_CAPACITY_MT = 35;

const isPincodeValid = (pincode) => /^\d{6}$/?.test(pincode);


// Step 1: Quantity selection is handled by BuySugarModal (parent)
// Step 2: Number of Trucks modal (this component, step='trucks')
// Step 3: Destination details + confirm (this component, step='destinations')

const SugarTruckMultiModal = ({ load, selectedQuantity: propSelectedQty, onClose, onBackToQuantity }) => {
  // step: 'trucks' | 'destinations'
  const [step, setStep] = useState('trucks');
  const [numTrucks, setNumTrucks] = useState(1);
  const [numTrucksInput, setNumTrucksInput] = useState('1');
  const [destinations, setDestinations] = useState([
    { id: 1, city: '', pincode: '', trucks: 1 }
  ]);
  const [booked, setBooked] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });
  // Payment state
  const [amountPaid, setAmountPaid] = useState('');
  const [amountPaidError, setAmountPaidError] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankNameError, setBankNameError] = useState('');
  const [utrNumber, setUtrNumber] = useState('');
  const [utrError, setUtrError] = useState('');
  const [tdsApplicable, setTdsApplicable] = useState(null);
  const [tdsError, setTdsError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');
  // Factory pincode map loaded asynchronously to avoid crash from sync Promise call
  const [factoryPincodeMap, setFactoryPincodeMap] = useState({});
  const navigate = useNavigate();

  // Use selectedQuantity from prop (passed from BuySugarModal step)
  const selectedQuantity = propSelectedQty || load?.quantity || 0;

  // Load factory pincodes asynchronously — NEVER call getFactories() synchronously
  useEffect(() => {
    let cancelled = false;
    getFactories()?.then((factories) => {
      if (cancelled) return;
      const map = {};
      (factories || [])?.forEach((factory) => {
        const pin = factory?.pincode?.toString()?.replace(/\D/g, '');
        if (factory?.name && pin?.length === 6) {
          map[factory.name] = pin;
          const city = factory?.location?.split(',')?.[0]?.trim();
          if (city) map[city] = pin;
          if (factory?.city) map[factory.city] = pin;
        }
      });
      setFactoryPincodeMap(map);
    })?.catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Resolve the factory's pincode: prefer load.pincode (from quota), then dynamic map, then static seeds
  const SEED_FACTORY_PINCODES = {
    'Kolhapur': '416001', 'Balrampur': '271201', 'Muzaffarnagar': '251001',
    'Mandya': '571401', 'Bijnor': '246701', 'Sangli': '416416',
    'Nellikuppam': '607105', 'Bardoli': '394601',
  };

  const fromCity = load?.city || '';
  const fromPincode = load?.loadingPincode ||
    load?.pincode ||
    factoryPincodeMap?.[load?.millName] ||
    factoryPincodeMap?.[fromCity] ||
    SEED_FACTORY_PINCODES?.[fromCity] ||
    '';

  // Sync destinations trucks count when numTrucks changes
  const handleNumTrucksChange = (val) => {
    const raw = val?.replace(/\D/g, '');
    setNumTrucksInput(raw);
    const n = parseInt(raw) || 0;
    setNumTrucks(n);
  };

  const handleNumTrucksBlur = () => {
    const n = Math.max(1, parseInt(numTrucksInput) || 1);
    setNumTrucks(n);
    setNumTrucksInput(String(n));
  };

  // Smart validation: trucks × 35 vs selectedQuantity
  const truckCapacityTotal = numTrucks * TRUCK_CAPACITY_MT;
  const showTruckWarning = numTrucks > 1;

  const handleProceedToDestinations = () => {
    const n = Math.max(1, parseInt(numTrucksInput) || 1);
    setNumTrucks(n);
    setNumTrucksInput(String(n));
    // Pre-fill destinations with the total trucks
    setDestinations([{ id: 1, city: '', pincode: '', trucks: n }]);
    setStep('destinations');
  };

  const handleBackToTrucks = () => {
    setStep('trucks');
  };

  const addDestination = () => {
    setDestinations(prev => [...prev, { id: Date.now(), city: '', pincode: '', trucks: 1 }]);
  };

  const removeDestination = (id) => {
    if (destinations?.length <= 1) return;
    setDestinations(prev => prev?.filter(d => d?.id !== id));
  };

  const fetchDistanceForDest = async (id, destPin) => {
    if (!fromPincode || fromPincode.length !== 6 || destPin.length !== 6) return;
    setDestinations(prev => prev.map(d => d.id === id ? { ...d, distLoading: true, lockedDist: null } : d));
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const resp = await fetch(`${supabaseUrl}/functions/v1/calculate-route-distance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ originPincode: fromPincode, destinationPincode: destPin }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setDestinations(prev => prev.map(d => d.id === id ? { ...d, lockedDist: data.distanceKm, distLoading: false } : d));
      } else {
        setDestinations(prev => prev.map(d => d.id === id ? { ...d, lockedDist: null, distLoading: false } : d));
      }
    } catch {
      setDestinations(prev => prev.map(d => d.id === id ? { ...d, lockedDist: null, distLoading: false } : d));
    }
  };

  const updateDestination = (id, field, value) => {
    setDestinations(prev => prev?.map(d => {
      if (d?.id !== id) return d;
      const updated = { ...d, [field]: value };
      if (field === 'pincode') {
        const pin = value?.replace(/\D/g, '').substring(0, 6);
        updated.pincode = pin;
        updated.lockedDist = null;
        updated.distLoading = false;
        if (pin.length === 6) fetchDistanceForDest(id, pin);
      }
      return updated;
    }));
  };

  const totalTrucks = destinations?.reduce((sum, d) => sum + (parseInt(d?.trucks) || 0), 0);

  // totalTransportCost — reserved for future fare calculation
  const totalTransportCost = 0;
  // Component-level totalAmount needed by payment step JSX
  const sugarTotal = (load?.rate || 0) * selectedQuantity;
  const totalAmount = sugarTotal + totalTransportCost;

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })?.format(amount);

  const handleConfirmBooking = async () => {
    const invalidDest = destinations?.find(d => !isPincodeValid(d?.pincode));
    if (invalidDest) return;

    // Validate total destination truck count doesn't exceed originally selected trucks
    const totalDestTrucks = destinations?.reduce((sum, d) => sum + (parseInt(d?.trucks) || 0), 0);
    if (totalDestTrucks > numTrucks) {
      setErrorModal({
        show: true,
        message: `Cannot assign ${totalDestTrucks} trucks across destinations. You selected ${numTrucks} truck${numTrucks > 1 ? 's' : ''} for this booking. Please reduce the truck count in your destinations.`,
      });
      return;
    }

    // Payment validation
    let paymentError = false;
    if (!amountPaid || Number(amountPaid) <= 0) { setAmountPaidError('Enter exact amount you transferred'); paymentError = true; } else setAmountPaidError('');
    if (!bankName?.trim()) { setBankNameError('Enter your bank name'); paymentError = true; } else setBankNameError('');
    if (!utrNumber?.trim()) { setUtrError('Enter your UTR number'); paymentError = true; } else setUtrError('');
    if (tdsApplicable === null) { setTdsError('Please select whether TDS is applicable'); paymentError = true; } else setTdsError('');
    if (!termsAccepted) { setTermsError('Please accept the Terms & Conditions to proceed'); paymentError = true; } else setTermsError('');
    if (paymentError) return;

    setBookingLoading(true);
    try {
      // 1. Auth check — direct Supabase session (works for email + Google OAuth)
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();

      if (sessionError) {
        console.error('Session error:', sessionError);
      }

      if (!session?.user?.id) {
        // Guest user — close this modal; parent handles guest modal
        onClose();
        return;
      }

      // 2. Quota approval check — re-fetch fresh status from Supabase
      let freshStatus = load?.availabilityStatus || 'available';
      if (load?.id) {
        const { data: quota } = await supabase
          ?.from('quotas')
          ?.select('availability_status')
          ?.eq('id', load?.id)
          ?.single();
        if (quota?.availability_status) freshStatus = quota?.availability_status;
      }

      // 3. Build destination summary
      const destinationSummary = destinations
        ?.filter(d => d?.pincode)
        ?.map(d => `${d?.city || 'Destination'} (${d?.pincode}) — ${d?.trucks} truck${d?.trucks > 1 ? 's' : ''}`)
        ?.join('; ');

      // Use selectedQuantity (buyer-chosen), NOT load.quantity (full quota)
      const sugarTotal = (load?.rate || 0) * selectedQuantity;
      const totalAmount = (sugarTotal * 1.05) + totalTransportCost;

      const bookingStatus = freshStatus === 'pending_approval' ? 'pending_approval' : 'pending';

      // 4. Save booking to Supabase buyer_bookings table (for both approved and pending_approval)
      const insertPayload = {
        buyer_id: session?.user?.id,
        buyer_email: session?.user?.email || '',
        quota_id: load?.id || null,
        offer_id: load?.offerId || '',
        factory_name: load?.millName || '',
        grade: load?.grade || '',
        quantity: Number(selectedQuantity) || 0,
        rate_per_ton: Number(load?.rate) || 0,
        loading_from: load?.deliveryFrom || load?.city || '',
        destination: destinationSummary || '',
        no_of_trucks: totalTrucks,
        total_amount: Number(totalAmount) || 0,
        booking_type: 'sugar_truck',
        booking_date: new Date()?.toISOString(),
        status: bookingStatus,
        tds_applicable: tdsApplicable,
        tds_amount: tdsApplicable ? Math.round(Number(selectedQuantity) * Number(load?.rate) * 0.001 * 100) / 100 : 0,
        net_payable: Math.round(((Number(selectedQuantity) * Number(load?.rate) * 1.05) - (tdsApplicable ? Math.round(Number(selectedQuantity) * Number(load?.rate) * 0.001 * 100) / 100 : 0)) * 100) / 100,
      };

      const { data: insertedData, error: insertError } = await supabase
        ?.from('buyer_bookings')
        ?.insert(insertPayload)
        ?.select();

      if (insertError) {
        console.error('Truck booking save error:', insertError?.message, insertError?.code, insertError?.details, insertError?.hint);
        setErrorModal({ show: true, message: 'Your booking could not be saved. Please try again.' });
        return;
      } else {
        console.log('Truck booking saved successfully:', insertedData);

        // Insert trade payment
        const tdsAmt = tdsApplicable ? Math.round(Number(selectedQuantity) * Number(load?.rate) * 0.001 * 100) / 100 : 0;
        const netPay = Math.round(((Number(selectedQuantity) * Number(load?.rate) * 1.05) - tdsAmt) * 100) / 100;
        try {
          await supabase?.from('trade_payments')?.insert({
            booking_id: insertedData?.[0]?.id,
            payment_type: 'buyer_advance',
            payment_method: 'RTGS',
            amount: Number(amountPaid),
            utr_number: utrNumber?.trim() || '',
            transaction_reference: utrNumber?.trim() || '',
            payer_bank_account: bankName?.trim() || '',
            paid_by: session?.user?.user_metadata?.full_name || session?.user?.email,
            paid_to: 'Villgrains FarmTech Pvt Ltd',
            status: 'pending',
            currency: 'INR',
          });
        } catch (paymentErr) {
          console.error('Trade payment insert error:', paymentErr);
        }

        // Send PO email to buyer + admin
        try {
          await supabase.functions.invoke('send-booking-email', {
            body: {
              buyer_email: session?.user?.email,
              buyer_name: session?.user?.user_metadata?.full_name || session?.user?.email,
              readable_booking_id: insertedData?.[0]?.readable_booking_id || insertedData?.[0]?.id?.slice(0, 8)?.toUpperCase(),
              readable_offer_id: load?.offerId || '',
              factory_name: load?.millName || '',
              factory_location: load?.deliveryFrom || load?.city || '',
              grade: load?.grade || '',
              quantity: Number(selectedQuantity),
              rate_per_ton: Number(load?.rate) || 0,
              total_amount: Number(totalAmount),
              booking_type: 'sugar_truck',
              vehicle_number: '',
              driver_mobile: '',
              bank_name: bankName?.trim() || '',
              utr_number: utrNumber?.trim() || '',
              amount_paid: Number(amountPaid),
              tds_applicable: tdsApplicable,
              tds_amount: tdsAmt,
              net_payable: netPay,
            },
          });
        } catch (emailErr) {
          console.error('PO email error:', emailErr);
        }
      }

      if (freshStatus === 'pending_approval') {
        setPendingApproval(true);
        return;
      }

      setBooked(true);
    } catch (err) {
      console.error('handleConfirmBooking error:', err?.message);
      setErrorModal({ show: true, message: 'An unexpected error occurred while saving your booking. Please try again.' });
    } finally {
      setBookingLoading(false);
    }
  };

  const allPincodesValid = destinations?.every(d => isPincodeValid(d?.pincode));

  // Pending Approval message screen
  if (pendingApproval) {
    return (
      <div className="fixed inset-0 z-[1500] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
        <div className="rounded-3xl shadow-2xl w-full max-w-md overflow-y-auto" style={{ backgroundColor: '#ffffff', maxHeight: '90vh' }}>
          {/* Amber/Orange header */}
          <div className="px-6 pt-6 pb-4 text-center" style={{ background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '3px solid rgba(255,255,255,0.4)' }}>
              <Icon name="Clock" size={28} color="#ffffff" />
            </div>
            <h2 className="text-xl font-bold text-white mb-1">Approval Pending</h2>
            <p className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.95)' }}>Your Sugar + Truck booking has been submitted and is awaiting approval.</p>
          </div>
          <div className="px-5 py-4">
            <div className="rounded-2xl p-4 mb-3 space-y-2" style={{ backgroundColor: '#fffbeb', border: '1.5px solid #fde68a' }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#92400e' }}>Booking Summary</p>
              {load?.offerId && (
                <div className="flex justify-between items-center text-sm">
                  <span style={{ color: '#6b7280' }}>Offer ID</span>
                  <span className="font-semibold" style={{ color: '#1f2937' }}>{load?.offerId}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Factory</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{load?.millName}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Grade</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{load?.grade}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Quantity</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{selectedQuantity} MT</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>No. of Trucks</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{totalTrucks} trucks</span>
              </div>
              <div className="flex justify-between items-center text-sm border-t pt-2" style={{ borderColor: '#fde68a' }}>
                <span style={{ color: '#6b7280' }}>Rate per Ton</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>₹{Number(load?.rate)?.toLocaleString('en-IN')}</span>
              </div>
            </div>
            <div className="rounded-xl p-3 mb-4 flex items-center gap-2" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
              <Icon name="Clock" size={14} color="#d97706" />
              <p className="text-xs font-medium" style={{ color: '#92400e' }}>Our team will confirm your booking within <strong>2 hours</strong></p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => navigate('/buyer-operations-dashboard')}
                className="py-2.5 rounded-xl text-sm font-bold border-2 transition-all"
                style={{ borderColor: '#d97706', color: '#d97706', backgroundColor: '#fffbeb' }}
              >
                View Booking
              </button>
              <button
                onClick={() => navigate('/buyer-operations-dashboard')}
                className="py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                style={{ backgroundColor: '#d97706' }}
              >
                Go to Buyer Dashboard
              </button>
            </div>
            <button
              onClick={onClose}
              className="w-full mt-2 py-2 text-xs font-medium rounded-xl"
              style={{ color: '#9ca3af', backgroundColor: '#f9fafb' }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Booking Confirmed screen
  if (booked) {
    return (
      <div className="fixed inset-0 z-[1500] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
        <div className="rounded-3xl shadow-2xl w-full max-w-md overflow-y-auto" style={{ backgroundColor: '#ffffff', maxHeight: '90vh' }}>
          {/* Amber/Orange header */}
          <div className="px-6 pt-6 pb-4 text-center" style={{ background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '3px solid rgba(255,255,255,0.4)' }}>
              <Icon name="Clock" size={28} color="#ffffff" />
            </div>
            <h2 className="text-xl font-bold text-white mb-1">Approval Pending</h2>
            <p className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.95)' }}>Your Sugar + Truck booking has been submitted and is awaiting approval.</p>
          </div>

          {/* Summary */}
          <div className="px-5 py-4">
            <div className="rounded-2xl p-4 mb-3 space-y-2" style={{ backgroundColor: '#fffbeb', border: '1.5px solid #fde68a' }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#92400e' }}>Booking Summary</p>
              {load?.offerId && (
                <div className="flex justify-between items-center text-sm">
                  <span style={{ color: '#6b7280' }}>Offer ID</span>
                  <span className="font-semibold" style={{ color: '#1f2937' }}>{load?.offerId}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Factory</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{load?.millName}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Grade</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{load?.grade}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Quantity Booked</span>
                <span className="font-bold" style={{ color: '#d97706' }}>{selectedQuantity} MT</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Loading From</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{load?.deliveryFrom || load?.city}</span>
              </div>
              <div className="border-t pt-2 space-y-1.5" style={{ borderColor: '#fde68a' }}>
                <p className="text-xs font-semibold" style={{ color: '#92400e' }}>Destinations</p>
                {destinations?.filter(d => d?.pincode)?.map((d, i) => (
                  <div key={d?.id} className="flex justify-between items-center text-xs">
                    <span style={{ color: '#6b7280' }}>{d?.city || `Destination ${i + 1}`} — {d?.pincode}</span>
                    <div className="flex items-center gap-2">
                      {d?.lockedDist && <span className="font-medium" style={{ color: '#d97706' }}>{d?.lockedDist} km</span>}
                      <span className="font-semibold" style={{ color: '#1f2937' }}>{d?.trucks} truck{d?.trucks > 1 ? 's' : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center text-sm border-t pt-2" style={{ borderColor: '#fde68a' }}>
                <span style={{ color: '#6b7280' }}>Total Trucks</span>
                <span className="font-bold text-base" style={{ color: '#d97706' }}>{totalTrucks} trucks</span>
              </div>
              <p className="text-xs" style={{ color: '#6b7280' }}>Up to 35 Ton — Average Truck Capacity 25T to 35T</p>
              {totalTransportCost > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span style={{ color: '#6b7280' }}>Est. Transport Cost</span>
                  <span className="font-bold" style={{ color: '#1f2937' }}>{formatCurrency(totalTransportCost)}</span>
                </div>
              )}
            </div>

            <div className="rounded-xl p-3 mb-4 flex items-center gap-2" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
              <Icon name="Clock" size={14} color="#d97706" />
              <p className="text-xs font-medium" style={{ color: '#92400e' }}>Our team will confirm your booking within <strong>2 hours</strong></p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => navigate('/buyer-operations-dashboard')}
                className="py-2.5 rounded-xl text-sm font-bold border-2 transition-all"
                style={{ borderColor: '#d97706', color: '#d97706', backgroundColor: '#fffbeb' }}
              >
                View Booking
              </button>
              <button
                onClick={() => navigate('/buyer-operations-dashboard')}
                className="py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                style={{ backgroundColor: '#d97706' }}
              >
                Go to Buyer Dashboard
              </button>
            </div>
            <button
              onClick={onClose}
              className="w-full mt-2 py-2 text-xs font-medium rounded-xl"
              style={{ color: '#9ca3af', backgroundColor: '#f9fafb' }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step: Number of Trucks
  if (step === 'trucks') {
    return (
      <div className="fixed inset-0 z-[1500] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="rounded-2xl shadow-xl w-full max-w-md overflow-hidden" style={{ backgroundColor: '#ffffff' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: '#f0e8de', backgroundColor: '#fffbeb' }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
                <Icon name="Truck" size={18} color="#d97706" />
              </div>
              <div>
                <h2 className="text-base font-bold" style={{ color: '#1f2937' }}>Number of Trucks</h2>
                <p className="text-xs" style={{ color: '#9ca3af' }}>{load?.millName} · {load?.grade} · {selectedQuantity} MT selected</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-amber-100">
              <Icon name="X" size={18} color="#6b7280" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            {/* Selected quantity reminder */}
            <div className="rounded-lg px-4 py-3 grid grid-cols-3 gap-3" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
              <div>
                <p className="text-xs font-medium" style={{ color: '#92400e' }}>Grade</p>
                <p className="text-sm font-bold" style={{ color: '#1f2937' }}>{load?.grade || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: '#92400e' }}>Qty Selected</p>
                <p className="text-sm font-bold" style={{ color: '#059669' }}>{selectedQuantity} MT</p>
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: '#92400e' }}>Rate / Ton</p>
                <p className="text-sm font-bold" style={{ color: '#1f2937' }}>{load?.rate ? `₹${Number(load?.rate)?.toLocaleString('en-IN')}` : '—'}</p>
              </div>
            </div>

            {/* Truck capacity note */}
            <div className="rounded-lg px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <Icon name="Truck" size={14} color="#059669" />
              <p className="text-xs font-medium" style={{ color: '#065f46' }}>
                Each truck carries up to <strong>35 MT</strong> (Average capacity 25T–35T)
              </p>
            </div>

            {/* Number of trucks input */}
            <div>
              <label className="block text-sm font-bold mb-3" style={{ color: '#374151' }}>
                How many trucks do you need?
              </label>
              <div className="flex items-stretch gap-0 rounded-2xl overflow-hidden border-2 border-amber-400 shadow-md">
                <button
                  type="button"
                  onClick={() => {
                    const n = Math.max(1, numTrucks - 1);
                    setNumTrucks(n);
                    setNumTrucksInput(String(n));
                  }}
                  className="flex items-center justify-center w-12 min-w-[44px] min-h-[52px] text-white text-3xl font-bold transition-colors select-none flex-shrink-0"
                  style={{ backgroundColor: '#1f2937' }}
                >
                  −
                </button>
                <input
                  type="text"
                  inputMode="numeric"
                  value={numTrucksInput}
                  onChange={e => handleNumTrucksChange(e?.target?.value)}
                  onBlur={handleNumTrucksBlur}
                  className="text-center text-2xl font-bold border-0 outline-none min-h-[52px] px-1 min-w-0 w-full"
                  style={{ backgroundColor: '#ffffff', color: '#1f2937' }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const n = numTrucks + 1;
                    setNumTrucks(n);
                    setNumTrucksInput(String(n));
                  }}
                  className="flex items-center justify-center w-12 min-w-[44px] min-h-[52px] text-white text-3xl font-bold transition-colors select-none flex-shrink-0"
                  style={{ backgroundColor: '#f59e0b' }}
                >
                  +
                </button>
              </div>
            </div>

            {/* Smart validation message */}
            {showTruckWarning && numTrucks >= 1 && (
              <div className="rounded-xl p-4" style={{ backgroundColor: '#fffbeb', border: '2px solid #fde68a' }}>
                <div className="flex items-start gap-2">
                  <Icon name="AlertTriangle" size={16} color="#d97706" className="mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-medium" style={{ color: '#92400e' }}>
                    You selected <strong>{selectedQuantity} MT</strong> whereas you are choosing{' '}
                    <strong>{numTrucks} trucks</strong> which may carry{' '}
                    <strong>35 × {numTrucks} = {truckCapacityTotal} Tons</strong>.{' '}
                    Are you sure you want to go ahead and book <strong>{numTrucks} trucks</strong>?
                  </p>
                </div>
              </div>
            )}

            {/* Capacity summary */}
            {numTrucks >= 1 && (
              <div className="rounded-lg px-4 py-3 flex justify-between items-center" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <span className="text-sm font-medium" style={{ color: '#065f46' }}>Max capacity ({numTrucks} trucks)</span>
                <span className="text-sm font-bold" style={{ color: '#059669' }}>{truckCapacityTotal} MT</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0" style={{ borderColor: '#f0e8de' }}>
            <button
              onClick={() => onBackToQuantity ? onBackToQuantity() : onClose()}
              className="flex-1 py-2.5 rounded-xl border text-sm font-medium"
              style={{ borderColor: '#d1c4b0', color: '#374151' }}
            >
              ← Back to Quantity
            </button>
            <button
              onClick={handleProceedToDestinations}
              disabled={!numTrucks || numTrucks < 1}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#d97706', color: '#ffffff' }}
            >
              Proceed →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step: Destinations + Confirm
  return (
    <>
      <div className="fixed inset-0 z-[1500] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" style={{ backgroundColor: '#ffffff' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: '#f0e8de', backgroundColor: '#fffbeb' }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
                <Icon name="MapPin" size={18} color="#d97706" />
              </div>
              <div>
                <h2 className="text-base font-bold" style={{ color: '#1f2937' }}>Destination Details</h2>
                <p className="text-xs" style={{ color: '#9ca3af' }}>{load?.millName} · {load?.offerId} · {selectedQuantity} MT · {numTrucks} truck{numTrucks > 1 ? 's' : ''}</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-amber-100">
              <Icon name="X" size={18} color="#6b7280" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Quota Summary */}
            <div className="rounded-lg px-4 py-3 grid grid-cols-4 gap-3" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
              <div>
                <p className="text-xs font-medium" style={{ color: '#92400e' }}>Grade</p>
                <p className="text-sm font-bold" style={{ color: '#1f2937' }}>{load?.grade || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: '#92400e' }}>Qty Booked</p>
                <p className="text-sm font-bold" style={{ color: '#059669' }}>{selectedQuantity} MT</p>
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: '#92400e' }}>Trucks</p>
                <p className="text-sm font-bold" style={{ color: '#1f2937' }}>{numTrucks}</p>
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: '#92400e' }}>Rate / Ton</p>
                <p className="text-sm font-bold" style={{ color: '#1f2937' }}>{load?.rate ? `₹${Number(load?.rate)?.toLocaleString('en-IN')}` : '—'}</p>
              </div>
            </div>

            {/* Truck capacity note */}
            <div className="rounded-lg px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <Icon name="Truck" size={14} color="#059669" />
              <p className="text-xs font-medium" style={{ color: '#065f46' }}>
                Up to 35 Ton — Average Truck Capacity 25T to 35T
              </p>
            </div>

            {/* From */}
            <div className="rounded-lg px-4 py-3 flex items-center gap-3" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <Icon name="MapPin" size={16} color="#059669" />
              <div>
                <p className="text-xs font-medium" style={{ color: '#065f46' }}>Loading From</p>
                <p className="text-sm font-bold" style={{ color: '#1f2937' }}>{load?.deliveryFrom || load?.city || '—'}</p>
              </div>
              {fromPincode && (
                <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#059669', color: '#ffffff' }}>
                  PIN: {fromPincode}
                </span>
              )}
            </div>

            {/* Destinations */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold" style={{ color: '#374151' }}>Destinations & Truck Split</p>
                <button
                  onClick={addDestination}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                  style={{ backgroundColor: '#d97706', color: '#ffffff' }}
                >
                  <Icon name="Plus" size={13} />
                  Add Destination
                </button>
              </div>

              <div className="space-y-3">
                {destinations?.map((dest, index) => {
                  const dist = dest?.lockedDist || null;
                  const pincodeValid = isPincodeValid(dest?.pincode);
                  return (
                    <div key={dest?.id} className="rounded-xl border p-4 space-y-3" style={{ backgroundColor: '#faf7f2', borderColor: '#e5d9cc' }}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                          Destination {index + 1}
                        </span>
                        {destinations?.length > 1 && (
                          <button onClick={() => removeDestination(dest?.id)} className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-red-100">
                            <Icon name="X" size={13} color="#dc2626" />
                          </button>
                        )}
                      </div>
                      {/* Destination / City (optional) */}
                      <div>
                        <label className="text-xs font-medium block mb-1" style={{ color: '#374151' }}>
                          Destination / City <span className="font-normal" style={{ color: '#9ca3af' }}>(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={dest?.city}
                          onChange={e => updateDestination(dest?.id, 'city', e?.target?.value)}
                          placeholder="e.g. Mumbai, Pune..."
                          className="w-full rounded-lg border px-3 py-2 text-sm"
                          style={{ borderColor: '#e5d9cc', backgroundColor: '#ffffff', color: '#1f2937' }}
                        />
                      </div>
                      {/* Pincode + Trucks */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium block mb-1" style={{ color: '#374151' }}>
                            Pincode <span style={{ color: '#dc2626' }}>*</span>
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={dest?.pincode}
                            onChange={e => updateDestination(dest?.id, 'pincode', e?.target?.value)}
                            placeholder="6-digit pincode"
                            className="w-full rounded-lg border px-3 py-2 text-sm font-semibold"
                            style={{
                              borderColor: dest?.pincode && !pincodeValid ? '#dc2626' : pincodeValid ? '#059669' : '#e5d9cc',
                              backgroundColor: '#ffffff',
                              color: '#1f2937'
                            }}
                          />
                          {dest?.pincode && !pincodeValid && (
                            <p className="text-[10px] mt-0.5" style={{ color: '#dc2626' }}>Enter valid 6-digit pincode</p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium block mb-1" style={{ color: '#374151' }}>No. of Trucks</label>
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={dest?.trucks}
                            onChange={e => updateDestination(dest?.id, 'trucks', e?.target?.value)}
                            className="w-full rounded-lg border px-3 py-2 text-sm font-semibold"
                            style={{ borderColor: '#e5d9cc', backgroundColor: '#ffffff', color: '#1f2937' }}
                          />
                        </div>
                      </div>
                      {pincodeValid && dest?.distLoading && (
                        <div className="flex items-center gap-2 py-1">
                          <div className="w-3 h-3 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
                          <p className="text-[10px]" style={{ color: '#9ca3af' }}>Calculating road distance...</p>
                        </div>
                      )}
                      {pincodeValid && !dest?.distLoading && dist !== null && (
                        <div className="rounded-lg p-2 text-center" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                          <p className="text-[10px] font-medium" style={{ color: '#9ca3af' }}>Road Distance (HGV)</p>
                          <p className="text-sm font-bold" style={{ color: '#059669' }}>{dist} km</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Totals */}
            {totalTrucks > 0 && (
              <div className="rounded-xl p-4 space-y-2" style={{ backgroundColor: '#fffbeb', border: '1.5px solid #fde68a' }}>
                <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#92400e' }}>Summary</p>
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6b7280' }}>Quantity Booked</span>
                  <span className="font-bold" style={{ color: '#059669' }}>{selectedQuantity} MT</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6b7280' }}>Total Trucks</span>
                  <span className="font-bold" style={{ color: '#1f2937' }}>{totalTrucks} trucks</span>
                </div>
                <p className="text-xs" style={{ color: '#b45309' }}>Up to 35 Ton — Average Truck Capacity 25T to 35T</p>
                {totalTransportCost > 0 && (
                  <div className="flex justify-between text-sm pt-2 border-t" style={{ borderColor: '#fde68a' }}>
                    <span style={{ color: '#6b7280' }}>Est. Total Transport Cost</span>
                    <span className="text-base font-extrabold" style={{ color: '#d97706' }}>{formatCurrency(totalTransportCost)}</span>
                  </div>
                )}
                <div className="mt-2 pt-2 border-t flex items-start gap-2" style={{ borderColor: '#fde68a' }}>
                  <Icon name="Info" size={13} color="#d97706" className="mt-0.5 flex-shrink-0" />
                  <p className="text-xs" style={{ color: '#92400e' }}>
                    Final transport rate and exact distance will be confirmed after booking with the transporter.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0" style={{ borderColor: '#f0e8de' }}>
            <button
              onClick={handleBackToTrucks}
              className="flex-1 py-2.5 rounded-xl border text-sm font-medium"
              style={{ borderColor: '#d1c4b0', color: '#374151' }}
            >
              ← Back to Trucks
            </button>
            <button
              onClick={() => { if (allPincodesValid) setStep('payment'); }}
              disabled={!allPincodesValid}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ backgroundColor: '#d97706', color: '#ffffff' }}
            >
              Next: Payment →
            </button>
          </div>
        </div>
      </div>
      {step === 'payment' && !booked && !pendingApproval && (
        <div className="fixed inset-0 z-[1500] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col" style={{ backgroundColor: '#ffffff' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: '#f0e8de', backgroundColor: '#fffbeb' }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
                  <Icon name="CreditCard" size={18} color="#d97706" />
                </div>
                <div>
                  <h2 className="text-base font-bold" style={{ color: '#1f2937' }}>Payment Details</h2>
                  <p className="text-xs" style={{ color: '#9ca3af' }}>{load?.millName} · {selectedQuantity} MT</p>
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-amber-100">
                <Icon name="X" size={18} color="#6b7280" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 p-6 space-y-4">

              {/* Order Summary */}
              <div className="rounded-xl p-4 space-y-2" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
                <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#92400e' }}>Order Summary</p>
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6b7280' }}>Quantity</span>
                  <span className="font-semibold" style={{ color: '#1f2937' }}>{selectedQuantity} MT</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6b7280' }}>Rate / MT</span>
                  <span className="font-semibold" style={{ color: '#1f2937' }}>₹{Number(load?.rate)?.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6b7280' }}>Total (Excl GST)</span>
                  <span className="font-semibold" style={{ color: '#1f2937' }}>₹{Number(totalAmount)?.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6b7280' }}>GST (5%)</span>
                  <span className="font-semibold" style={{ color: '#1f2937' }}>₹{(Number(totalAmount) * 0.05)?.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-sm border-t pt-2" style={{ borderColor: '#fde68a' }}>
                  <span className="font-bold" style={{ color: '#92400e' }}>Total (Incl GST 5%)</span>
                  <span className="font-bold text-base" style={{ color: '#d97706' }}>₹{(Number(totalAmount) * 1.05)?.toLocaleString('en-IN')}</span>
                </div>
              </div>

              {/* Amount Paid */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold" style={{ color: '#374151' }}>Amount Paid <span style={{ color: '#ef4444' }}>*</span></label>
                <p className="text-xs" style={{ color: '#9ca3af' }}>Enter exact amount you transferred</p>
                <input type="number" placeholder="e.g. 1245000" value={amountPaid}
                  onChange={e => { setAmountPaid(e?.target?.value); setAmountPaidError(''); }}
                  className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  style={{ borderColor: amountPaidError ? '#ef4444' : '#d1d5db', color: '#1f2937' }} />
                {amountPaidError && <p className="text-xs" style={{ color: '#dc2626' }}>{amountPaidError}</p>}
              </div>

              {/* Bank Name */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold" style={{ color: '#374151' }}>Bank Name <span style={{ color: '#ef4444' }}>*</span></label>
                <p className="text-xs" style={{ color: '#9ca3af' }}>e.g. ICICI Bank, HDFC Bank</p>
                <input type="text" placeholder="e.g. ICICI Bank" value={bankName}
                  onChange={e => { setBankName(e?.target?.value); setBankNameError(''); }}
                  className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  style={{ borderColor: bankNameError ? '#ef4444' : '#d1d5db', color: '#1f2937' }} />
                {bankNameError && <p className="text-xs" style={{ color: '#dc2626' }}>{bankNameError}</p>}
              </div>

              {/* UTR Number */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold" style={{ color: '#374151' }}>UTR Number <span style={{ color: '#ef4444' }}>*</span></label>
                <p className="text-xs" style={{ color: '#9ca3af' }}>Enter exact UTR number from your bank</p>
                <input type="text" placeholder="e.g. ICICIUTR0001234567" value={utrNumber}
                  onChange={e => { setUtrNumber(e?.target?.value?.toUpperCase()); setUtrError(''); }}
                  className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  style={{ borderColor: utrError ? '#ef4444' : '#d1d5db', color: '#1f2937' }} />
                {utrError && <p className="text-xs" style={{ color: '#dc2626' }}>{utrError}</p>}
              </div>

              {/* TDS */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold" style={{ color: '#374151' }}>TDS Applicable u/s 194Q <span style={{ color: '#ef4444' }}>*</span></label>
                <p className="text-xs" style={{ color: '#9ca3af' }}>Select Yes if your annual purchases exceed ₹50 Lakhs</p>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="tds_truck" checked={tdsApplicable === true}
                      onChange={() => { setTdsApplicable(true); setTdsError(''); }}
                      className="w-4 h-4 accent-amber-500" />
                    <span className="text-sm font-medium" style={{ color: '#1f2937' }}>Yes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="tds_truck" checked={tdsApplicable === false}
                      onChange={() => { setTdsApplicable(false); setTdsError(''); }}
                      className="w-4 h-4 accent-amber-500" />
                    <span className="text-sm font-medium" style={{ color: '#1f2937' }}>No</span>
                  </label>
                </div>
                {tdsError && <p className="text-xs" style={{ color: '#dc2626' }}>{tdsError}</p>}
              </div>

              {/* Net Payable */}
              {tdsApplicable !== null && (
                <div className="rounded-xl p-4" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                  {tdsApplicable ? (
                    <>
                      <div className="flex justify-between text-sm mb-1">
                        <span style={{ color: '#6b7280' }}>TDS u/s 194Q @ 0.1%</span>
                        <span style={{ color: '#dc2626' }}>- ₹{(Math.round(Number(selectedQuantity) * Number(load?.rate) * 0.001 * 100) / 100)?.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-bold text-sm" style={{ color: '#065f46' }}>Net Payable (After TDS)</span>
                        <span className="font-bold text-base" style={{ color: '#059669' }}>
                          ₹{(Math.round((((sugarTotal * 1.05) + totalTransportCost) - (Math.round(Number(selectedQuantity) * Number(load?.rate) * 0.001 * 100) / 100)) * 100) / 100)?.toLocaleString('en-IN')}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between">
                      <span className="font-bold text-sm" style={{ color: '#065f46' }}>Amount Payable</span>
                      <span className="font-bold text-base" style={{ color: '#059669' }}>₹{((sugarTotal * 1.05) + totalTransportCost)?.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                </div>
              )}

              {/* T&C */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={termsAccepted}
                  onChange={e => { setTermsAccepted(e?.target?.checked); setTermsError(''); }}
                  className="w-4 h-4 mt-0.5 accent-amber-500 flex-shrink-0" />
                <span className="text-xs" style={{ color: '#374151' }}>
                  I agree to the{' '}
                  <a href="https://sugarhathi.com/buyerterms" target="_blank" rel="noopener noreferrer"
                    className="underline font-semibold" style={{ color: '#d97706' }}>
                    Buyer Terms & Conditions
                  </a>
                </span>
              </label>
              {termsError && <p className="text-xs" style={{ color: '#dc2626' }}>{termsError}</p>}

            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0" style={{ borderColor: '#f0e8de' }}>
              <button onClick={() => setStep('destinations')}
                className="flex-1 py-2.5 rounded-xl border text-sm font-medium"
                style={{ borderColor: '#d1c4b0', color: '#374151' }}>
                ← Back
              </button>
              <button onClick={handleConfirmBooking} disabled={bookingLoading}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ backgroundColor: '#d97706', color: '#ffffff' }}>
                {bookingLoading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Booking...</>
                ) : 'Confirm Booking'}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'trucks' && !booked && !pendingApproval && (
        <div className="fixed inset-0 z-[1500] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="rounded-2xl shadow-xl w-full max-w-md overflow-hidden" style={{ backgroundColor: '#ffffff' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: '#f0e8de', backgroundColor: '#fffbeb' }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
                  <Icon name="Truck" size={18} color="#d97706" />
                </div>
                <div>
                  <h2 className="text-base font-bold" style={{ color: '#1f2937' }}>Number of Trucks</h2>
                  <p className="text-xs" style={{ color: '#9ca3af' }}>{load?.millName} · {load?.grade} · {selectedQuantity} MT selected</p>
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-amber-100">
                <Icon name="X" size={18} color="#6b7280" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Selected quantity reminder */}
              <div className="rounded-lg px-4 py-3 grid grid-cols-3 gap-3" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
                <div>
                  <p className="text-xs font-medium" style={{ color: '#92400e' }}>Grade</p>
                  <p className="text-sm font-bold" style={{ color: '#1f2937' }}>{load?.grade || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium" style={{ color: '#92400e' }}>Qty Selected</p>
                  <p className="text-sm font-bold" style={{ color: '#059669' }}>{selectedQuantity} MT</p>
                </div>
                <div>
                  <p className="text-xs font-medium" style={{ color: '#92400e' }}>Rate / Ton</p>
                  <p className="text-sm font-bold" style={{ color: '#1f2937' }}>{load?.rate ? `₹${Number(load?.rate)?.toLocaleString('en-IN')}` : '—'}</p>
                </div>
              </div>

              {/* Truck capacity note */}
              <div className="rounded-lg px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <Icon name="Truck" size={14} color="#059669" />
                <p className="text-xs font-medium" style={{ color: '#065f46' }}>
                  Each truck carries up to <strong>35 MT</strong> (Average capacity 25T–35T)
                </p>
              </div>

              {/* Number of trucks input */}
              <div>
                <label className="block text-sm font-bold mb-3" style={{ color: '#374151' }}>
                  How many trucks do you need?
                </label>
                <div className="flex items-stretch gap-0 rounded-2xl overflow-hidden border-2 border-amber-400 shadow-md">
                  <button
                    type="button"
                    onClick={() => {
                      const n = Math.max(1, numTrucks - 1);
                      setNumTrucks(n);
                      setNumTrucksInput(String(n));
                    }}
                    className="flex items-center justify-center w-12 min-w-[44px] min-h-[52px] text-white text-3xl font-bold transition-colors select-none flex-shrink-0"
                    style={{ backgroundColor: '#1f2937' }}
                  >
                    −
                  </button>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={numTrucksInput}
                    onChange={e => handleNumTrucksChange(e?.target?.value)}
                    onBlur={handleNumTrucksBlur}
                    className="text-center text-2xl font-bold border-0 outline-none min-h-[52px] px-1 min-w-0 w-full"
                    style={{ backgroundColor: '#ffffff', color: '#1f2937' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const n = numTrucks + 1;
                      setNumTrucks(n);
                      setNumTrucksInput(String(n));
                    }}
                    className="flex items-center justify-center w-12 min-w-[44px] min-h-[52px] text-white text-3xl font-bold transition-colors select-none flex-shrink-0"
                    style={{ backgroundColor: '#f59e0b' }}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Smart validation message */}
              {showTruckWarning && numTrucks >= 1 && (
                <div className="rounded-xl p-4" style={{ backgroundColor: '#fffbeb', border: '2px solid #fde68a' }}>
                  <div className="flex items-start gap-2">
                    <Icon name="AlertTriangle" size={16} color="#d97706" className="mt-0.5 flex-shrink-0" />
                    <p className="text-sm font-medium" style={{ color: '#92400e' }}>
                      You selected <strong>{selectedQuantity} MT</strong> whereas you are choosing{' '}
                      <strong>{numTrucks} trucks</strong> which may carry{' '}
                      <strong>35 × {numTrucks} = {truckCapacityTotal} Tons</strong>.{' '}
                      Are you sure you want to go ahead and book <strong>{numTrucks} trucks</strong>?
                    </p>
                  </div>
                </div>
              )}

              {/* Capacity summary */}
              {numTrucks >= 1 && (
                <div className="rounded-lg px-4 py-3 flex justify-between items-center" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                  <span className="text-sm font-medium" style={{ color: '#065f46' }}>Max capacity ({numTrucks} trucks)</span>
                  <span className="text-sm font-bold" style={{ color: '#059669' }}>{truckCapacityTotal} MT</span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0" style={{ borderColor: '#f0e8de' }}>
              <button
                onClick={() => onBackToQuantity ? onBackToQuantity() : onClose()}
                className="flex-1 py-2.5 rounded-xl border text-sm font-medium"
                style={{ borderColor: '#d1c4b0', color: '#374151' }}
              >
                ← Back to Quantity
              </button>
              <button
                onClick={handleProceedToDestinations}
                disabled={!numTrucks || numTrucks < 1}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#d97706', color: '#ffffff' }}
              >
                Proceed →
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Error Modal */}
      <ErrorModal
        show={errorModal?.show}
        message={errorModal?.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />
    </>
  );
};

export default SugarTruckMultiModal;


